import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { subscriptionRepository } from "../repositories/subscription.repository";
import type { SubscriptionUsageResponse } from "../types/subscription.types";
import { useCachedMedium } from "./useCachedMedium";
import { useCachedTerreiro } from "./useCachedTerreiro";
import { getSubscriptionLimitsEnabled } from "./useEnhancedSubscriptionFeatures";

/**
 * Query key helper for subscription usage
 * Supports both terreiro and medium contexts
 */
export const subscriptionUsageQueryKey = (context?: {
  terreiroId?: string;
  mediumId?: string;
}) => {
  const id = context?.terreiroId ?? context?.mediumId ?? "no-context";
  const type = context?.terreiroId
    ? "terreiro"
    : context?.mediumId
      ? "medium"
      : "unknown";
  return ["subscription", type, id, "usage"] as const;
};

interface UseSubscriptionUsageQueryProps {
  /** Override the terreiro ID (defaults to cached terreiro) */
  terreiroId?: string;
  /** Override the medium ID (defaults to cached medium) */
  mediumId?: string;
  /** Enable/disable the query */
  enabled?: boolean;
}

/**
 * Hook to fetch usage statistics for all subscription features
 *
 * Supports both terreiro and personal (medium) contexts.
 * Automatically detects context from cached data if not provided.
 *
 * Returns current usage counts, limits, and percentages for all
 * countable features. Useful for displaying a usage dashboard
 * or identifying features nearing their limits.
 *
 * @example
 * ```tsx
 * // Auto-detect context from cache
 * const { data: usage, isLoading } = useSubscriptionUsageQuery();
 *
 * // Explicit terreiro context
 * const { data: usage } = useSubscriptionUsageQuery({ terreiroId: 'terreiro-123' });
 *
 * // Explicit medium context
 * const { data: usage } = useSubscriptionUsageQuery({ mediumId: 'medium-456' });
 *
 * // Display usage for events
 * const eventsUsage = usage?.usage.events;
 * console.log(`${eventsUsage?.current} / ${eventsUsage?.limit} events used`);
 * ```
 */
export function useSubscriptionUsageQuery(
  props: UseSubscriptionUsageQueryProps = {},
) {
  const {
    terreiroId: overrideTerreiroId,
    mediumId: overrideMediumId,
    enabled = true,
  } = props;
  const cachedTerreiro = useCachedTerreiro();
  const cachedMedium = useCachedMedium();

  const callerManaged =
    Object.prototype.hasOwnProperty.call(props, "terreiroId") ||
    Object.prototype.hasOwnProperty.call(props, "mediumId");

  // When caller provides explicit overrides, use ONLY those (don't mix with stale cached values).
  // This prevents race conditions when SubscriptionContext passes atomic context.
  const terreiroId = callerManaged ? overrideTerreiroId : cachedTerreiro?.id;
  const mediumId = callerManaged ? overrideMediumId : cachedMedium?.id;

  // Determine personal profile mode:
  // - Caller-managed: personal if no terreiroId provided
  // - Self-managed: personal if cachedTerreiro is explicitly null
  const isPersonalProfile = callerManaged
    ? !overrideTerreiroId
    : cachedTerreiro === null;

  // Prioritize terreiro context if both exist, BUT respect personal profile mode
  const hasTerreiro = !isPersonalProfile && !!terreiroId;
  const hasMedium = (isPersonalProfile || !hasTerreiro) && !!mediumId;

  // Build context params for API call
  const contextParams = hasTerreiro
    ? { terreiroId }
    : hasMedium
      ? { mediumId }
      : undefined;

  const shouldEnableQuery =
    enabled && getSubscriptionLimitsEnabled() && (hasTerreiro || hasMedium);

  // Debug: Log enablement conditions
  if (typeof window !== "undefined" && window.__DEV_SUBSCRIPTION_USAGE__) {
    console.log("[useSubscriptionUsageQuery] Enablement gates:", {
      enabled,
      featureFlagEnabled: getSubscriptionLimitsEnabled(),
      hasTerreiro,
      hasMedium,
      shouldEnableQuery,
      contextParams,
      overrideTerreiroId,
      overrideMediumId,
      isPersonalProfile,
    });
  }

  return useQuery<SubscriptionUsageResponse, Error>({
    queryKey: subscriptionUsageQueryKey(contextParams),
    queryFn: () => subscriptionRepository.getUsage(contextParams),
    staleTime: 1000 * 60 * 2, // 2 minutes (usage changes frequently)
    gcTime: 1000 * 60 * 30, // 30 minutes
    refetchOnMount: "always",
    enabled: shouldEnableQuery,
    retry: (failureCount, error) => {
      // Don't retry on 404 - API not implemented yet
      if (isAxiosError(error) && error.response?.status === 404) {
        return false;
      }
      return failureCount < 2;
    },

    retryDelay: (attemptIndex) => {
      return Math.min(1000 * 2 ** attemptIndex, 4000);
    },

    refetchOnWindowFocus: true,

    meta: {
      errorMessage: "Failed to load subscription usage",
    },
  });
}

/**
 * Helper to get features that are nearing their limits
 *
 * @param usage - The usage response from useSubscriptionUsageQuery
 * @returns Array of [featureName, stats] tuples for features at >= 80% usage
 */
export function getFeaturesNearingLimit(
  usage: SubscriptionUsageResponse | undefined,
) {
  if (!usage?.usage) return [];

  return Object.entries(usage.usage).filter(
    ([, stats]) => stats.nearingLimit && stats.limit !== -1,
  );
}

/**
 * Helper to get features that have reached their limits
 *
 * @param usage - The usage response from useSubscriptionUsageQuery
 * @returns Array of [featureName, stats] tuples for features at 100% usage
 */
export function getFeaturesAtLimit(
  usage: SubscriptionUsageResponse | undefined,
) {
  if (!usage?.usage) return [];

  return Object.entries(usage.usage).filter(
    ([, stats]) => stats.limit !== -1 && stats.current >= stats.limit,
  );
}
